import React from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'

const NotFoundPage = () => {
  const navigate = useNavigate()
  const { isAuthenticated } = useAuth()

  const handleGoBack = () => {
    navigate(-1)
  }

  const handleGoHome = () => {
    if (isAuthenticated) {
      navigate('/dashboard')
    } else {
      navigate('/')
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center px-4">
      <div className="max-w-2xl w-full text-center">
        {/* 404 Illustration */}
        <div className="mb-12">
          <div className="relative">
            {/* Large 404 Text */}
            <h1 className="text-9xl md:text-[12rem] font-bold text-gray-100 select-none leading-none">
              404
            </h1>
            {/* Floating Elements */}
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
              <div className="text-6xl animate-bounce">🎬</div>
            </div>
          </div>
        </div>

        {/* Error Message */}
        <div className="mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Oops! Page Not Found
          </h2>
          <p className="text-xl text-gray-600 mb-4 max-w-lg mx-auto">
            The page you're looking for seems to have wandered off into the digital void.
          </p>
          <p className="text-gray-500 max-w-md mx-auto">
            Don't worry, even the best video editors sometimes lose track of their clips!
            Let's get you back on track.
          </p>
        </div>

        {/* Action Buttons */}
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={handleGoHome}
              className="px-8 py-4 bg-blue-600 text-white rounded-xl text-lg font-semibold hover:bg-blue-700 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center space-x-2"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
              </svg>
              <span>{isAuthenticated ? 'Go to Dashboard' : 'Go Home'}</span>
            </button>

            <button
              onClick={handleGoBack}
              className="px-8 py-4 border-2 border-gray-300 text-gray-700 rounded-xl text-lg font-semibold hover:border-gray-400 hover:bg-gray-50 transition-all duration-200 flex items-center justify-center space-x-2"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
              </svg>
              <span>Go Back</span>
            </button>
          </div>

          {/* Quick Links */}
          {isAuthenticated && (
            <div className="mt-8 pt-6 border-t border-gray-200">
              <p className="text-sm text-gray-500 mb-4">Or try these popular sections:</p>
              <div className="flex flex-wrap justify-center gap-3">
                <Link
                  to="/dashboard?tab=templates"
                  className="px-4 py-2 text-sm bg-white text-gray-700 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors duration-200"
                >
                  📚 Templates
                </Link>
                <Link
                  to="/dashboard?tab=photos"
                  className="px-4 py-2 text-sm bg-white text-gray-700 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors duration-200"
                >
                  📸 Photos
                </Link>
                <Link
                  to="/dashboard?tab=videos"
                  className="px-4 py-2 text-sm bg-white text-gray-700 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors duration-200"
                >
                  🎬 Videos
                </Link>
                <Link
                  to="/apps"
                  className="px-4 py-2 text-sm bg-white text-gray-700 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors duration-200"
                >
                  🚀 Apps
                </Link>
              </div>
            </div>
          )}
        </div>

        {/* Fun Fact */}
        <div className="mt-12 p-6 bg-white/70 rounded-2xl border border-white/30 backdrop-blur-sm shadow-lg">
          <div className="flex items-start space-x-3">
            <div className="text-2xl">💡</div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-1">Did You Know?</h3>
              <p className="text-sm text-gray-600">
                The first 404 error was discovered at CERN in 1992. The room where the first web server was located was number 404!
                Now you're part of internet history too.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default NotFoundPage
